import React, { useState, useEffect } from "react";
import { API } from "../../../Services/axios";
import { URLS } from "../../../Services/Urls";
import { getProductImageUrl, getFallbackImageUrl } from "../../../utils/productImage";

const ChoiceProductDisplay = ({ choiceId, productRef, onRemove }) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productRef) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Try to get product by ID
        const response = await API.get(URLS.PRODUCT.getProductById(productRef));
        setProduct(response.data);
        setError(null);
      } catch (err) {
        console.error(`Erreur lors de la récupération du produit ${productRef}:`, err);
        setError("Produit non trouvé");
        // Try to search for the product as fallback
        try {
          const searchResponse = await API.get(URLS.PRODUCT.getProduct(productRef.toString(), 1));
          if (searchResponse.data?.items && searchResponse.data.items.length > 0) {
            const foundProduct = searchResponse.data.items.find(p => p.id === productRef);
            if (foundProduct) {
              setProduct(foundProduct);
              setError(null);
            }
          }
        } catch (searchErr) {
          console.error("Erreur lors de la recherche du produit:", searchErr);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productRef]);

  if (loading) {
    return (
      <div className="choice-products-list mt-2">
        <small className="text-muted d-block mb-2">Chargement du produit...</small>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="choice-products-list mt-2">
        <small className="text-muted d-block mb-2">
          Produit associé : ID {productRef}
        </small>
        <div className="d-flex align-items-center border rounded p-2">
          <div className="flex-grow-1">
            <small className="text-danger">Produit non trouvé (ID: {productRef})</small>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      </div>
    );
  }

  const resolvedProduct =
    product?.product || product; // support nested shape { product: {...} }

  const normalizeText = (value) => {
    if (value === undefined || value === null) return null;
    if (typeof value === "string" || typeof value === "number") return String(value);
    // If the API wraps the value inside an object (e.g. { value: "..." })
    if (typeof value === "object" && "value" in value) {
      const nested = value.value;
      if (typeof nested === "string" || typeof nested === "number") return String(nested);
    }
    return null;
  };

  const nameCandidates = [
    resolvedProduct?.name,
    resolvedProduct?.title,
    resolvedProduct?.product_name,
    Array.isArray(resolvedProduct?.custom_attributes)
      ? resolvedProduct.custom_attributes.find((attr) => attr?.attribute_code === "name")?.value
      : null,
  ];

  const productName =
    nameCandidates.map(normalizeText).find(Boolean) || `Produit ${productRef}`;

  const productPrice = resolvedProduct?.price ?? resolvedProduct?.final_price ?? resolvedProduct?.price_ht;

  const imageUrl = getProductImageUrl(resolvedProduct) || getFallbackImageUrl();

  return (
    <div className="choice-products-list mt-2">
      <small className="text-muted d-block mb-2">
        Produit associé :
      </small>
      <div className="d-flex align-items-center border rounded p-2">
        <img
          src={imageUrl}
          alt={productName}
          style={{ width: "40px", height: "40px", objectFit: "cover" }}
          className="rounded me-2"
        />
        <div className="flex-grow-1">
          <small className="d-block">{productName}</small>
          <small className="text-muted">
            {productPrice !== undefined && productPrice !== null ? `£${productPrice}` : ""}
          </small>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <i className="bi bi-x"></i>
        </button>
      </div>
    </div>
  );
};

export default ChoiceProductDisplay;

